// frontend/src/components/video/controls/keymap.ts

/**
 * Canonical keyboard map shared by every video player surface (issue #1206).
 *
 * This module is the normative reference: players resolve keys through
 * `resolveVideoKeyAction` instead of matching raw keys, so the same key means
 * the same kind of action everywhere. The per-context realization may differ
 * (the analytics player seeks the telemetry timeline; the per-file modal maps
 * large seeks to playlist segments), but the keys themselves must not.
 *
 * | Action            | Keys             | Notes                                   |
 * |-------------------|------------------|-----------------------------------------|
 * | play-pause        | p, Space         | all players                             |
 * | seek-back/-forward| ←, →             | per-surface step, default 2 s           |
 * | seek-*-large      | Shift+←, Shift+→ | per-surface step, default 10 s          |
 * | toggle-mute       | m                | all players                             |
 * | toggle-fullscreen | f                | all players                             |
 *
 * Combinations carrying Ctrl, Cmd (Meta), or Alt are never consumed; Shift
 * only selects the large-seek variant of the arrow keys. Capital letters are
 * not matched, so Shift+letter keeps its native behavior.
 *
 * Live players handle play-pause/mute/fullscreen only (no timeline to seek).
 * Analytics-only keys (j/k chart scroll, +/- zoom, i/o chart height) are
 * bound separately in useAnalyticsKeyboardShortcuts and intentionally not
 * modeled here.
 */
export type VideoKeyAction =
  | "play-pause"
  | "seek-back"
  | "seek-forward"
  | "seek-back-large"
  | "seek-forward-large"
  | "toggle-mute"
  | "toggle-fullscreen";

/** Structural subset of KeyboardEvent, so React synthetic events work too. */
export interface VideoKeyEvent {
  key: string;
  shiftKey: boolean;
  /** Shortcut combinations with browser-level modifiers are never ours. */
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

export function resolveVideoKeyAction(
  event: VideoKeyEvent,
): VideoKeyAction | null {
  // Ctrl/Cmd/Alt combinations belong to the browser and the OS (Find, Print,
  // history, text shortcuts); the video keymap must never consume them.
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return null;
  }
  switch (event.key) {
    case "p":
    case " ":
      return "play-pause";
    case "ArrowLeft":
      return event.shiftKey ? "seek-back-large" : "seek-back";
    case "ArrowRight":
      return event.shiftKey ? "seek-forward-large" : "seek-forward";
    case "m":
      return "toggle-mute";
    case "f":
      return "toggle-fullscreen";
    default:
      return null;
  }
}

/** Elements whose native keyboard behavior must never be hijacked. */
export function isNativeKeyTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return Boolean(
    element &&
      (element.tagName === "INPUT" ||
        element.tagName === "TEXTAREA" ||
        element.tagName === "SELECT" ||
        element.isContentEditable),
  );
}
